export type VariantDefinitions = Record<string, Record<string, string | Record<string, string>>>;

/**
 * Conditional style - applies styles to one element based on the state of another element.
 *
 * @example
 * {
 *   when: { element: 'detailsSummary', state: 'hover' },
 *   classes: 'rotate-45'
 * }
 * // Generates: hover:[&_.e-menu-item-details-trailing-icon]:rotate-45
 * // Applied to: detailsSummary
 */
export interface ConditionalStyle {
  when: {
    element: string;
    state: string;
  };
  classes: string;
}

/**
 * Compound variant definition - applies classes when multiple variant conditions match.
 * Supports single values, arrays of values, wildcards, and exclusions.
 *
 * @example
 * {
 *   color: 'primary',         // Single value - exact match
 *   size: ['md', 'lg', 'xl'], // Array - matches any of these sizes
 *   class: 'shadow-lg'
 * }
 *
 * @example
 * {
 *   color: 'mono',  // Single value
 *   variant: '*',   // Wildcard - matches all variants
 *   class: 'text-white'
 * }
 *
 * @example
 * {
 *   color: 'neutral',
 *   variant: ['*', '!solid'],  // Matches all variants except solid
 *   class: 'special-styling'
 * }
 */
export type CompoundVariant<TVariants extends VariantDefinitions> = Partial<{
  [K in keyof TVariants]: keyof TVariants[K] | Array<keyof TVariants[K] | '*' | `!${string}`> | '*';
}> & {
  class: string | Record<string, string | ConditionalStyle | (string | ConditionalStyle)[]>;
};

/**
 * Config type that allows base, variants, defaults, compoundVariants,
 * plus any additional string properties for sub-elements.
 */
interface VariantConfig<
  TVariants extends VariantDefinitions,
  TDefaults extends Partial<{
    [K in keyof TVariants]: keyof TVariants[K];
  }>
> {
  base?: string;
  variants: TVariants;
  defaults?: TDefaults;
  compoundVariants?: CompoundVariant<TVariants>[];
  elementPrefix?: string; // Configurable class name prefix for conditional styles
  [key: string]: any; // Allow any additional properties
}

/**
 * Creates a typed utility for composing Tailwind/class strings from variant definitions.
 *
 * @typeParam TVariants - Map of variant names to their possible values and class names.
 * @typeParam TDefaults - Optional map of default variant values.
 *
 * @param config.base - Base class string always included.
 * @param config.variants - Variant definitions: { size: { sm: '...', md: '...' }, ... }.
 * @param config.defaults - Default variant values applied when not overridden.
 * @param config.compoundVariants - Array of compound variants that apply classes when multiple conditions match.
 * @param config[key: string] - Additional string properties for sub-elements (e.g., header, footer, list).
 *
 * @returns A function that:
 * - When no additional properties: returns a string with base + variant classes
 * - When additional properties exist: returns an object with `.base` and other properties
 * - Exposes the original `variants` and `defaults` as static properties
 *
 * @example
 * // Without additional properties - returns string
 * const buttonClasses = defineVariants({
 *   base: 'px-4 py-2',
 *   variants: { theme: { light: 'bg-white', dark: 'bg-gray-900' } }
 * });
 * buttonClasses({ theme: 'dark' }) // Returns string: 'px-4 py-2 bg-gray-900'
 *
 * @example
 * // With additional properties - returns object
 * const menuClasses = defineVariants({
 *   base: 'flex flex-col',
 *   header: 'px-4 py-2 border-b',
 *   footer: 'px-4 py-2 border-t',
 *   variants: { theme: { light: 'bg-white', dark: 'bg-gray-900' } }
 * });
 * menuClasses({ theme: 'dark' }).base   // Returns base classes
 * menuClasses({ theme: 'dark' }).header // Returns header classes
 *
 * **Class Priority Order** (highest priority last):
 * 1. Base classes
 * 2. Variant 'default' classes (if defined)
 * 3. Specific variant classes
 * 4. **Compound variant classes (HIGHEST PRIORITY)** - always applied last to allow overriding
 */

/**
 * Type guard to detect conditional style objects.
 */
function isConditionalStyle(value: any): value is ConditionalStyle {
  return (
    typeof value === 'object' &&
    value !== null &&
    'when' in value &&
    'classes' in value &&
    typeof value.when === 'object' &&
    'element' in value.when &&
    'state' in value.when
  );
}

/**
 * Convert camelCase to kebab-case.
 * Handles leading uppercase: Icon → icon (not -icon)
 *
 * @example
 * camelToKebab('detailsTrailingIcon') // 'details-trailing-icon'
 * camelToKebab('Icon') // 'icon'
 */
function camelToKebab(str: string): string {
  return str
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, ''); // Remove leading dash if present
}

/**
 * Generate Tailwind selector for conditional styles.
 * Splits multiple classes and generates a separate selector for each.
 *
 * @param targetElement - The element being styled (e.g., 'detailsTrailingIcon')
 * @param triggerState - The CSS pseudo-class (e.g., 'hover', 'focus')
 * @param targetClasses - The classes to apply (e.g., 'rotate-45' or 'text-blue-600 font-semibold')
 * @param elementPrefix - The class name prefix (e.g., 'e-menu-item')
 * @returns Tailwind selectors (e.g., 'hover:[&_.e-menu-item-details-trailing-icon]:rotate-45')
 *
 * @example
 * generateConditionalSelector('detailsTrailingIcon', 'hover', 'rotate-45', 'e-menu-item')
 * // Returns: 'hover:[&_.e-menu-item-details-trailing-icon]:rotate-45'
 *
 * @example
 * generateConditionalSelector('label', 'hover', 'text-blue-600 font-semibold', 'e-component')
 * // Returns: 'hover:[&_.e-component-label]:text-blue-600 hover:[&_.e-component-label]:font-semibold'
 */
function generateConditionalSelector(
  targetElement: string,
  triggerState: string,
  targetClasses: string,
  elementPrefix: string
): string {
  // Convert camelCase to kebab-case for class names
  const kebabTarget = camelToKebab(targetElement);
  const targetClass = `.${elementPrefix}-${kebabTarget}`;

  // Split multiple classes and generate a selector for each
  const classes = targetClasses.trim().split(/\s+/);
  return classes.map(cls => `${triggerState}:[&_${targetClass}]:${cls}`).join(' ');
}

export function defineVariants<
  TVariants extends VariantDefinitions,
  TDefaults extends Partial<{
    [K in keyof TVariants]: keyof TVariants[K];
  }>
>(config: VariantConfig<TVariants, TDefaults>) {
  type VariantProps = {
    [K in keyof TVariants]?: keyof TVariants[K];
  };

  // Extract additional properties (everything except known config keys)
  const knownKeys = new Set(['base', 'variants', 'defaults', 'compoundVariants']);
  const additionalKeys: string[] = [];

  for (const key in config) {
    if (!knownKeys.has(key)) {
      additionalKeys.push(key);
    }
  }

  // Helper function to check if compound variant matches
  const matchesCompound = (compound: any, merged: any) => {
    for (const key in compound) {
      if (key === 'class') continue;

      const compoundValue = compound[key];
      const mergedValue = merged[key];

      // Support wildcards, arrays with exclusions, and single values
      if (compoundValue === '*') {
        continue;
      } else if (Array.isArray(compoundValue)) {
        const hasWildcard = compoundValue.includes('*' as any);

        if (hasWildcard) {
          const exclusions = compoundValue
            .filter((v): v is string => typeof v === 'string' && v.startsWith('!'))
            .map(v => v.slice(1));

          if (exclusions.includes(mergedValue as string)) {
            return false;
          }
        } else {
          if (!compoundValue.includes(mergedValue as any)) {
            return false;
          }
        }
      } else {
        if (compoundValue !== mergedValue) {
          return false;
        }
      }
    }
    return true;
  };

  // Helper function to compute classes for a specific target (base, header, footer, etc.)
  const computeClasses = (target: string, props: VariantProps) => {
    const merged = { ...config.defaults, ...props };
    const classes: string[] = [];

    // Add static base classes for this target
    if (target === 'base' && config.base) {
      classes.push(config.base);
    } else if (target !== 'base' && config[target]) {
      classes.push(config[target] as string);
    }

    // Apply variants
    for (const key in merged) {
      const value = merged[key];
      if (!value) continue;

      const variantKey = key as keyof TVariants;
      const variantValue = value as keyof TVariants[typeof variantKey];

      // Apply 'default' classes if they exist for this variant group
      const defaultClasses =
        config.variants[variantKey]?.['default' as keyof TVariants[typeof variantKey]];
      if (defaultClasses) {
        if (typeof defaultClasses === 'string') {
          if (target === 'base') classes.push(defaultClasses);
        } else if (typeof defaultClasses === 'object' && defaultClasses[target]) {
          classes.push(defaultClasses[target]);
        }
      }

      // Apply specific variant classes (skip if the value is 'default' itself)
      if (variantValue !== 'default') {
        const variantClasses = config.variants[variantKey]?.[variantValue];
        if (variantClasses) {
          if (typeof variantClasses === 'string') {
            // String variant - only applies to base
            if (target === 'base') classes.push(variantClasses);
          } else if (typeof variantClasses === 'object' && variantClasses[target]) {
            // Object variant - apply to specific target
            classes.push(variantClasses[target]);
          }
        }
      }
    }

    // Apply compound variants (ALWAYS LAST for highest priority)
    if (config.compoundVariants) {
      for (const compound of config.compoundVariants) {
        if (matchesCompound(compound, merged)) {
          const compoundClass = compound.class;

          if (typeof compoundClass === 'string') {
            // String class - only applies to base
            if (target === 'base') classes.push(compoundClass);
          } else if (typeof compoundClass === 'object') {
            // Object class - handle each property
            for (const [key, value] of Object.entries(compoundClass)) {
              // Handle arrays of mixed strings and conditional styles
              if (Array.isArray(value)) {
                for (const item of value) {
                  if (isConditionalStyle(item)) {
                    // Apply to trigger element, not target element
                    if (target === item.when.element) {
                      const selector = generateConditionalSelector(
                        key,
                        item.when.state,
                        item.classes,
                        config.elementPrefix || 'e-component'
                      );
                      classes.push(selector);
                    }
                  } else if (typeof item === 'string' && key === target) {
                    // Regular string in array - apply to matching target
                    classes.push(item);
                  }
                }
              } else if (isConditionalStyle(value)) {
                // Single conditional style - apply to trigger element
                if (target === value.when.element) {
                  const selector = generateConditionalSelector(
                    key,
                    value.when.state,
                    value.classes,
                    config.elementPrefix || 'e-component'
                  );
                  classes.push(selector);
                }
              } else if (typeof value === 'string' && key === target) {
                // Regular string - apply to matching target
                classes.push(value);
              }
            }
          }
        }
      }
    }

    return classes.join(' ').trim();
  };

  const variantFn = (props: VariantProps = {}) => {
    const baseClasses = computeClasses('base', props);

    // If there are no additional keys, just return the string
    if (additionalKeys.length === 0) {
      return baseClasses;
    }

    // Create a plain object with explicit properties
    const result: any = {
      base: baseClasses
    };

    // Add properties for all additional keys (header, footer, etc.)
    for (const key of additionalKeys) {
      result[key] = computeClasses(key, props);
    }

    return result;
  };

  // Attach static properties to the function
  return Object.assign(variantFn, {
    variants: config.variants,
    defaults: config.defaults,
    compoundVariants: config.compoundVariants
  });
}
